import React, { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import useHasValueChanged from 'react-hooks/useHasValueChanged';
import useTheme from 'branding/theme';
import useOutsideClick from '../../../hooks/useOutsideClick';
import ListTrigger from './ListTrigger';
import Option from '../../Option';
import { processChildren, getSelectedLabelText } from './utils';
import handleKeyboardEvent from './utils/handleKeyboardEvent';
import handleKeyDownPropagation from './utils/handleKeyDownPropagation';
import useKeyupPrevention from './utils/useKeyUpPrevention';
import useToggleOffOnClick from './utils/useToggleOffOnClick';
import useAutoScroll from './utils/useAutoScroll';
import handleChildClick from './utils/handleChildClick';
import useValueChange from './utils/useValueChange';
/** not to be used outside of the Select component */
const ListBehavior = ({ children, name, isFocused, isToggled, setOn, setOff, handleFocus, handleBlur, visibleLimit, disabled, dropdownDirection, defaultValue = '', errorId, onChange, optionLabelStyles }) => {
    const { setValue } = useFormContext();
    const isScrollable = visibleLimit > 0;
    const [ref, isOutsideClick] = useOutsideClick(isToggled);
    const [selectedValue, setSelectedValue] = useState(defaultValue);
    const [isEnterPress, setIsEnterPress] = useState(false);
    const [isEscOrTabPress, setIsEscOrTabPress] = useState(false);
    const processedChildren = processChildren(children, name, disabled, selectedValue, setSelectedValue, isScrollable);
    const selectedLabelText = getSelectedLabelText(processedChildren, selectedValue);
    const hasValueChanged = useHasValueChanged(selectedValue);
    const { borders, colors } = useTheme();
    useValueChange({ selectedValue, hasValueChanged, name, setValue, onChange });
    useKeyupPrevention(name);
    useToggleOffOnClick({
        name,
        hasValueChanged,
        isOutsideClick,
        isEnterPress,
        setIsEnterPress,
        isEscOrTabPress,
        setIsEscOrTabPress,
        setOff
    });
    useAutoScroll({
        name,
        isOpen: isToggled,
        elements: processedChildren,
        selectedValue
    });
    const handleChildElClick = handleChildClick({
        name,
        selectedValue,
        setIsEnterPress,
        setIsEscOrTabPress,
        setOn,
        setOff
    });
    const handleKeyUp = useMemo(() => handleKeyboardEvent({
        elements: processedChildren,
        selectedValue,
        setSelectedValue,
        isOpen: isToggled,
        setOpen: setOn,
        setClosed: setOff,
        setIsEnterPress,
        setIsEscOrTabPress,
        name
    }), [processedChildren, selectedValue, isToggled, setOn, setOff, name]);
    const handleKeyDown = useMemo(() => handleKeyDownPropagation({ isOpen: isToggled, name }), [isToggled, name]);
    const placeholderStyles = useMemo(() => ({
        ...(isScrollable && isToggled && { borderBottom: '0.2rem solid' })
    }), [isScrollable, isToggled]);
    const detailsStyles = useMemo(() => ({
        position: 'absolute',
        ...(dropdownDirection === 'up'
            ? { bottom: `-${borders.base.borderWidth}` }
            : {
                top: `-${borders.base.borderWidth}`
            }),
        left: `-${borders.base.borderWidth}`,
        width: '100%',
        minHeight: '100%',
        boxSizing: 'content-box',
        borderRadius: '2.6rem',
        ...borders.base,
        ...(!isFocused && !isToggled && borders.variant.secondary),
        // error styles MUST come after active styles to override them
        ...(!!errorId && borders.error),
        ...(isToggled && { backgroundColor: colors.neutrals.background, zIndex: 1 }),
        ...(visibleLimit > 0 && {
            maxHeight: `${visibleLimit * 4.8}rem`,
            overflowY: 'auto',
            overflowX: 'hidden'
        })
    }), [borders, colors, dropdownDirection, errorId, isFocused, isToggled, visibleLimit]);
    return (React.createElement("div", { ref: ref, style: { position: 'relative' } },
        React.createElement(Option, { labelText: selectedLabelText, value: "", isInitial: true, isPlaceholder: true, styles: placeholderStyles, optionLabelStyles: optionLabelStyles }),
        React.createElement("details", { id: name, onClick: handleChildElClick, onKeyUp: handleKeyUp, onKeyDown: handleKeyDown, style: detailsStyles },
            React.createElement(ListTrigger, { handleFocus: handleFocus, handleBlur: handleBlur, disabled: disabled, isScrollable: isScrollable }, selectedLabelText),
            React.createElement("div", { style: { marginTop: `-${borders.base.borderWidth}` } }, processedChildren))));
};
export default ListBehavior;
//# sourceMappingURL=index.js.map